import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { useAuth } from '@/context/AuthContext'
import { captureMessage, addBreadcrumb } from '@/utils/sentry'
import type { MachineWithMetrics, Metrics, VoipStates, VoipState } from '@/types'

export interface WebSocketAlert {
  id: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  alert_type: string
  message: string
  machine_id: string
  triggered_at: string
}

interface WebSocketContextType {
  machines: MachineWithMetrics[]
  voipStates: VoipStates
  isConnected: boolean
  isReconnecting: boolean
  error: string | null
  lastAlert: WebSocketAlert | null
  onNewAlert: (callback: (alert: WebSocketAlert) => void) => () => void
  getVoipStateByExtension: (extension: string) => VoipState | null
  getVoipStateByOperatorId: (operatorId: number) => VoipState | null
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

interface InitialStateMessage {
  type: 'initial_state'
  machines: MachineWithMetrics[]
  voip_states?: VoipStates
}

interface MetricsUpdateMessage {
  type: 'metrics_update'
  machine_id: string
  data: Partial<Metrics>
}

interface StatusChangeMessage {
  type: 'status_change'
  machine_id: string
  status: 'online' | 'offline'
  operator_name?: string
}

interface PingMessage {
  type: 'ping'
}

interface AlertMessage {
  type: 'alert'
  data: WebSocketAlert
}

interface VoipUpdateMessage {
  type: 'voip_update'
  event_type: string
  extension: string
  data: VoipState
  timestamp: string
}

type WebSocketMessage = InitialStateMessage | MetricsUpdateMessage | StatusChangeMessage | PingMessage | AlertMessage | VoipUpdateMessage

// Try to exchange a refresh token for a fresh access token. Used when the
// WebSocket gets closed with 4001 — rather than give up and force a page
// reload, we silently renew the JWT the same way the Axios interceptor
// does and retry. Matches the endpoint used in api/client.ts.
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return null
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api'
  try {
    const res = await fetch(`${apiBase}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return null
    const data = await res.json()
    const newAccess = data?.access_token
    if (!newAccess) return null
    localStorage.setItem('access_token', newAccess)
    if (data?.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token)
    }
    return newAccess
  } catch {
    return null
  }
}

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [machines, setMachines] = useState<MachineWithMetrics[]>([])
  const [voipStates, setVoipStates] = useState<VoipStates>({})
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [lastAlert, setLastAlert] = useState<WebSocketAlert | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectAttemptsRef = useRef<number>(0)
  const alertCallbacksRef = useRef<Set<(alert: WebSocketAlert) => void>>(new Set())
  // Unmount guard — stops reconnection loops from firing after the
  // provider has been torn down (e.g. on logout). Set to true when the
  // effect's cleanup runs.
  const unmountedRef = useRef<boolean>(false)

  // Register callback for new alerts
  const onNewAlert = useCallback((callback: (alert: WebSocketAlert) => void) => {
    alertCallbacksRef.current.add(callback)
    return () => {
      alertCallbacksRef.current.delete(callback)
    }
  }, [])

  // Get VoIP state by extension
  const getVoipStateByExtension = useCallback((extension: string): VoipState | null => {
    return voipStates[extension] || null
  }, [voipStates])

  // Get VoIP state by operator ID
  const getVoipStateByOperatorId = useCallback((operatorId: number): VoipState | null => {
    const entry = Object.entries(voipStates).find(
      ([, state]) => state.operator_id === operatorId
    )
    return entry ? entry[1] : null
  }, [voipStates])

  const scheduleReconnect = useCallback((delayMs: number) => {
    if (unmountedRef.current) return
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null
      if (!unmountedRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        connect()
      }
    }, delayMs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
  }, [])

  const startHeartbeat = useCallback((ws: WebSocket) => {
    stopHeartbeat()
    // Client-side keepalive every 25s. Backstop against idle proxies
    // (Nginx default 60s, Cloudflare 100s) even if the server's own
    // ping cadence hiccups. "ping" is a plain text message the server
    // treats as a liveness signal — see dashboard_ws.py.
    heartbeatIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send('ping')
        } catch {
          // Socket in an odd state; let onclose handle reconnection.
        }
      }
    }, 25000)
  }, [stopHeartbeat])

  const connect = useCallback(async () => {
    if (unmountedRef.current) return

    // Always read the freshest token from localStorage rather than closing
    // over a React state value that may be stale after an Axios silent
    // refresh. This single change prevents "zombie socket" bugs where the
    // provider keeps retrying with an access token that expired 20 min ago
    // while localStorage already holds a valid refreshed one.
    const token = localStorage.getItem('access_token')
    if (!token) {
      console.log('WebSocket: No token available, cannot connect')
      setError('Non authentifié')
      return
    }

    // If we already have an open connection, don't stack another one on
    // top of it. Provides idempotency against double-mounts (StrictMode)
    // or accidental double-connects from timer races.
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      return
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const wsUrl = `${protocol}//${host}/ws/dashboard?token=${encodeURIComponent(token)}`

    console.log('WebSocket connecting:', { protocol, host, hasToken: !!token, attempt: reconnectAttemptsRef.current })
    addBreadcrumb({
      category: 'websocket',
      message: 'Connecting to WebSocket',
      level: 'info',
      data: { url: wsUrl.replace(/token=.*/, 'token=***'), protocol, host, attempt: reconnectAttemptsRef.current }
    })
    setIsReconnecting(true)

    try {
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        setIsConnected(true)
        setIsReconnecting(false)
        setError(null)
        reconnectAttemptsRef.current = 0
        startHeartbeat(ws)
        console.log('WebSocket connected')
      }

      ws.onmessage = (event) => {
        // Server echoes 'pong' as plain text when the client heartbeat
        // sends 'ping' — swallow that before attempting JSON.parse so it
        // doesn't spam the console with parse errors every 25s.
        if (typeof event.data === 'string' && (event.data === 'pong' || event.data === 'ping')) {
          return
        }
        try {
          const message: WebSocketMessage = JSON.parse(event.data)

          switch (message.type) {
            case 'initial_state':
              setMachines(message.machines)
              if (message.voip_states) {
                setVoipStates(message.voip_states)
                console.log(`Received initial VoIP states: ${Object.keys(message.voip_states).length} extensions`)
              }
              console.log(`Received initial state: ${message.machines.length} machines`)
              break

            case 'metrics_update':
              setMachines(prev => prev.map(m => {
                if (m.machine_id === message.machine_id) {
                  return {
                    ...m,
                    metrics: m.metrics
                      ? { ...m.metrics, ...message.data }
                      : message.data as Metrics
                  }
                }
                return m
              }))
              break

            case 'status_change':
              setMachines(prev => prev.map(m => {
                if (m.machine_id === message.machine_id) {
                  return {
                    ...m,
                    status: message.status
                  }
                }
                return m
              }))
              console.log(`Status change: ${message.machine_id} -> ${message.status}`)
              break

            case 'ping':
              try {
                if (ws.readyState === WebSocket.OPEN) {
                  ws.send('pong')
                }
              } catch {
                // Socket closing; onclose will trigger reconnect
              }
              break

            case 'alert':
              setLastAlert(message.data)
              // Notify all registered callbacks
              alertCallbacksRef.current.forEach(callback => {
                try {
                  callback(message.data)
                } catch (e) {
                  console.error('Error in alert callback:', e)
                }
              })
              console.log(`New alert received: ${message.data.alert_type} - ${message.data.severity}`)
              break

            case 'voip_update':
              setVoipStates(prev => ({
                ...prev,
                [message.extension]: message.data
              }))
              console.log(`VoIP update: ${message.extension} -> ${message.data.status}`)
              break
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
        }
      }

      ws.onclose = async (event) => {
        setIsConnected(false)
        setIsReconnecting(false)
        stopHeartbeat()

        const closeInfo = {
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          url: wsUrl.replace(/token=.*/, 'token=***')
        }
        console.log(`WebSocket closed - code: ${event.code}, reason: ${event.reason}, wasClean: ${event.wasClean}`)

        addBreadcrumb({
          category: 'websocket',
          message: `WebSocket closed (code: ${event.code})`,
          level: event.code === 1000 ? 'info' : 'warning',
          data: closeInfo
        })

        if (unmountedRef.current || event.code === 1000) {
          // Clean close — user logged out or provider unmounted
          return
        }

        // Auth-denial codes: the token we used is expired or wrong.
        // Don't give up — the refresh_token in localStorage is likely
        // still valid. Swap it for a fresh access token and retry.
        // THIS is the fix for the "dashboard disconnects after 20-30 min
        // and never recovers" bug: previously 4001 was a dead end.
        if (event.code === 4001 || event.code === 403) {
          const refreshed = await refreshAccessToken()
          if (refreshed) {
            setError('Token expiré, renouvellement...')
            reconnectAttemptsRef.current = 0
            scheduleReconnect(500)
            return
          }
          setError('Authentification échouée')
          captureMessage('WebSocket auth failed and refresh exhausted', 'error', closeInfo)
          return
        }

        // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s.
        // Reset to 0 on successful onopen. Max 20 attempts (~10 min of
        // backing off) before giving up; user can F5 if that happens.
        reconnectAttemptsRef.current += 1
        if (reconnectAttemptsRef.current > 20) {
          setError('Impossible de rétablir la connexion. Rechargez la page.')
          captureMessage('WebSocket reconnect attempts exhausted', 'error', closeInfo)
          return
        }
        const backoffMs = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current - 1))
        setError(`Connexion perdue, reconnexion dans ${Math.round(backoffMs / 1000)}s...`)
        // Only sample Sentry on the first couple of drops to avoid noise
        if (reconnectAttemptsRef.current <= 2 && event.code !== 1006 && event.code !== 1005) {
          captureMessage(`WebSocket closed unexpectedly (${event.code})`, 'warning', closeInfo)
        }
        scheduleReconnect(backoffMs)
      }

      ws.onerror = () => {
        setIsReconnecting(false)
        setError('Erreur de connexion WebSocket')
        console.error('WebSocket error')
        // Don't schedule reconnect here — onclose always fires after
        // onerror and owns the backoff policy.
      }

      wsRef.current = ws
    } catch {
      setError('Impossible de se connecter au WebSocket')
      reconnectAttemptsRef.current += 1
      const backoffMs = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current - 1))
      scheduleReconnect(backoffMs)
    }
  }, [scheduleReconnect, startHeartbeat, stopHeartbeat])

  useEffect(() => {
    if (!isAuthenticated) return

    unmountedRef.current = false
    reconnectAttemptsRef.current = 0
    connect()

    return () => {
      unmountedRef.current = true
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      stopHeartbeat()
      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'provider unmount')
        } catch {
          // ignore
        }
        wsRef.current = null
      }
    }
  }, [isAuthenticated, connect, stopHeartbeat])

  return (
    <WebSocketContext.Provider value={{
      machines,
      voipStates,
      isConnected,
      isReconnecting,
      error,
      lastAlert,
      onNewAlert,
      getVoipStateByExtension,
      getVoipStateByOperatorId,
    }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocket() {
  const context = useContext(WebSocketContext)
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
